import { createAsyncThunk } from '@reduxjs/toolkit';
import { getLoadTime } from '../../widgets/messenger/components/Sidebar/KeywordSearch/helper';
import { keywordSearchSlice } from './index';
import { ReduxState } from 'redux-stores';

export const EVENTS = {
  OPEN_CONV_FOR_KWS_MESSAGE: 'openConvForKWSMessage',
  OPEN_CONV_FOR_ENTITY: 'openConvForEntity',
  CONV_LOADED_ALREADY_OPEN: 'convForKWSMessageLoadedAlreadyOpen',
  CONV_LOADED_FRESH: 'convForKWSMessageLoadedFresh',
  MESSAGE_SCROLL_SUCCESS: 'messageScrollSuccess',
  MESSAGE_SCROLL_FAIL: 'messageScrollFail',
  EXIT_KEYWORD_SEARCH: 'exitKeywordSearchFlow',
  COMPOSE_TO_SEARCH_ENTITY_SUCCESS: 'composeToSearchEntitySuccess',
  COMPOSE_TO_SEARCH_ENTITY_FAIL: 'composeToSearchEntityFail',
  OPEN_CONV_FOR_KWS_MESSAGE_FAIL: 'openConvForKWSMessageFail',
} as const;

const JUMP_OUTCOMES = {
  CONV_SUCCESS: 'convsuccess',
  MESSAGE_SUCCESS: 'messagesuccess',
  FAIL: 'fail',
  EXIT: 'exit',
} as const;

const { setLoadMessageStartTime, setLoadConversationStartTime, setLoadConversationFinishTime } =
  keywordSearchSlice.actions;

export const trackSearchLoadEvent = createAsyncThunk(
  'keywordSearch/trackSearchLoadEvent',
  async (event: string, thunkAPI) => {
    const state = thunkAPI.getState() as ReduxState;
    const { trackerStore } = window.mobxStores;
    const { loadMessageStartTime, loadConversationStartTime, loadConversationFinishTime } =
      state.keywordSearch;

    const currentTime = performance.now();

    // Handle initial conversation opening events
    if (event === EVENTS.OPEN_CONV_FOR_KWS_MESSAGE) {
      thunkAPI.dispatch(setLoadConversationStartTime(currentTime));
      thunkAPI.dispatch(setLoadMessageStartTime(currentTime));
      return;
    }

    if (event === EVENTS.OPEN_CONV_FOR_ENTITY) {
      thunkAPI.dispatch(setLoadConversationStartTime(currentTime));
      return;
    }

    // Handle conversation loading completion events
    if (event === EVENTS.CONV_LOADED_ALREADY_OPEN && loadConversationStartTime) {
      thunkAPI.dispatch(setLoadConversationFinishTime(0));
      return;
    }

    if (event === EVENTS.CONV_LOADED_FRESH && loadConversationStartTime) {
      const conversationLoadTime = getLoadTime(loadConversationStartTime);
      thunkAPI.dispatch(setLoadConversationFinishTime(conversationLoadTime));
      return;
    }

    // Handle message scroll events
    if (
      event === EVENTS.MESSAGE_SCROLL_SUCCESS &&
      loadMessageStartTime &&
      loadConversationFinishTime !== null
    ) {
      const messageLoadTime = getLoadTime(loadMessageStartTime);
      trackerStore.logInboxJumpToConversation?.({
        JumpLoadOutcome: JUMP_OUTCOMES.MESSAGE_SUCCESS,
        MessageLoadTime: messageLoadTime,
        ConversationLoadTime: loadConversationFinishTime,
      });
      thunkAPI.dispatch(setLoadMessageStartTime(null));
      return;
    }

    if (event === EVENTS.MESSAGE_SCROLL_FAIL && loadConversationFinishTime) {
      trackerStore.logInboxJumpToConversation?.({
        JumpLoadOutcome: JUMP_OUTCOMES.FAIL,
        ConversationLoadTime: loadConversationFinishTime,
      });
      thunkAPI.dispatch(setLoadMessageStartTime(null));
      return;
    }

    // Handle exit keyword search flow
    if (
      event === EVENTS.EXIT_KEYWORD_SEARCH &&
      loadMessageStartTime &&
      loadConversationFinishTime
    ) {
      const messageLoadTime = getLoadTime(loadMessageStartTime);
      trackerStore.logInboxJumpToConversation?.({
        JumpLoadOutcome: JUMP_OUTCOMES.EXIT,
        MessageLoadTime: messageLoadTime,
        ConversationLoadTime: loadConversationFinishTime,
      });
      thunkAPI.dispatch(setLoadMessageStartTime(null));
      thunkAPI.dispatch(setLoadConversationStartTime(null));
      thunkAPI.dispatch(setLoadConversationFinishTime(null));
      return;
    }

    // Handle compose to search entity events
    if (event.startsWith('composeToSearchEntity') && loadConversationStartTime) {
      const conversationLoadTime = getLoadTime(loadConversationStartTime);
      const jumpLoadOutcome =
        event === EVENTS.COMPOSE_TO_SEARCH_ENTITY_SUCCESS
          ? JUMP_OUTCOMES.CONV_SUCCESS
          : JUMP_OUTCOMES.FAIL;

      trackerStore.logInboxJumpToConversation({
        JumpLoadOutcome: jumpLoadOutcome,
        ConversationLoadTime: conversationLoadTime,
      });

      thunkAPI.dispatch(setLoadConversationStartTime(null));
      return;
    }

    // Handle failures when opening a conversation from a keyword search result
    if (event === EVENTS.OPEN_CONV_FOR_KWS_MESSAGE_FAIL && loadConversationStartTime) {
      const conversationLoadTime = getLoadTime(loadConversationStartTime);
      trackerStore.logInboxJumpToConversation?.({
        JumpLoadOutcome: JUMP_OUTCOMES.FAIL,
        ConversationLoadTime: conversationLoadTime,
      });

      thunkAPI.dispatch(setLoadConversationStartTime(null));
      return;
    }
  }
);
